import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useApplications } from "@/hooks/useApplications";
import { useDailyMatches } from "@/hooks/useDailyMatches";
import { useMultiCV } from "@/hooks/useMultiCV";
import type { ChatApplication, ChatMatch, ChatProfileSummary } from "@/lib/findigoChat/types";
import { logError } from "@/lib/logger";

export const useFindigoChatData = () => {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id || null);
    };
    loadUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { data: profile } = useUserProfile();
  const { applications, loading: applicationsLoading } = useApplications();
  const {
    dailyMatches,
    isLoading: matchesLoading,
    refreshMatches,
  } = useDailyMatches();
  const { hasCV, loading: cvLoading } = useMultiCV();

  const pendingAttemptsQuery = useQuery({
    queryKey: ["findigo-chat-pending-attempts", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { count, error } = await supabase
        .from("apply_attempts")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .in("status", ["pending", "processing"]);

      if (error) {
        logError("Chat pending attempts fetch failed", error);
        return null;
      }

      return count ?? 0;
    },
    enabled: !!userId,
    staleTime: 1000 * 60,
  });

  const profileSummary: ChatProfileSummary | null = profile
    ? {
        location: profile.location,
        remoteOk: profile.remote_ok,
        canTravel: profile.can_travel ?? null,
        onboardingCompleted: profile.onboarding_completed ?? null,
        roles: profile.roles || [],
        skillsWanted: profile.skills_wanted || [],
      }
    : null;

  const mappedApplications: ChatApplication[] = applications.map((app) => ({
    jobId: app.job_id,
    appliedAt: app.applied_at,
    status: app.status,
    title: app.job_title ?? null,
    company: app.company_name ?? null,
  }));

  const mappedMatches: ChatMatch[] = (dailyMatches || []).map((match) => ({
    id: match.id,
    title: match.title,
    company: match.company,
    matchScore: match.matchScore,
    publishedAt: match.published_at || null,
    firstSeenAt: match.first_seen_at || null,
    matchReasons: match.matchReasons || null,
    applyUrl: match.apply_url || null,
    sourceUrl: match.url || null,
    autoApplyReady: match.auto_apply_ready ?? null,
  }));

  return {
    profileSummary,
    applications: mappedApplications,
    applicationsLoading,
    matches: mappedMatches,
    matchesLoading,
    refreshMatches,
    hasCV: cvLoading ? null : hasCV,
    pendingAttemptsCount: pendingAttemptsQuery.data ?? null,
    pendingAttemptsLoading: pendingAttemptsQuery.isLoading,
  };
};
